import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Zap, Sun, Moon, Menu, X, Github, Youtube, Linkedin, MessageCircle } from "lucide-react";
import { usePublicTheme } from "../lib/theme";
import { useAuth } from "../context/AuthContext";
import { ChatWidget } from "./ChatWidget";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/features", label: "Features" },
  { to: "/gallery", label: "Gallery" },
  { to: "/code", label: "Code" },
  { to: "/publications", label: "Publications" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export function PublicNav() {
  const { theme, toggle } = usePublicTheme();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setHidden(y > lastY.current && y > 64);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-40 backdrop-blur-xl bg-[var(--bg)]/80 border-b border-[var(--bd)] transition-transform duration-300 ${hidden ? "-translate-y-full" : "translate-y-0"}`}>
      <div className="max-w-[1300px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="font-heading font-black text-sm sm:text-lg md:text-xl tracking-tighter flex items-center gap-1.5 sm:gap-2" data-testid="public-logo">
          <Zap size={18} className="text-[var(--accent)] sm:w-5 sm:h-5" /> Campus Energy
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
              className={`px-2.5 py-2 rounded-md text-sm transition-colors whitespace-nowrap ${loc.pathname === n.to ? "text-[var(--t1)]" : "text-[var(--t2)] hover:text-[var(--t1)]"}`}>
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button onClick={toggle} data-testid="public-theme-toggle" className="w-9 h-9 rounded-md border border-[var(--bd)] flex items-center justify-center hover:border-[var(--bd-strong)] transition-colors">
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          {user ? (
            <Link to="/app" data-testid="nav-dashboard" className="px-4 py-2 rounded-md bg-[var(--accent)] text-black text-sm font-medium">Dashboard</Link>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="hidden sm:inline-flex px-4 py-2 rounded-md border border-[var(--bd)] text-sm font-medium hover:border-[var(--bd-strong)] transition-colors">Sign In</Link>
              <Link to="/register" data-testid="nav-register" className="px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-md bg-[var(--accent)] text-black text-xs sm:text-sm font-medium active:scale-95 transition-transform">Get Started</Link>
            </>
          )}
          <button onClick={() => setOpen((o) => !o)} className="lg:hidden w-9 h-9 rounded-md border border-[var(--bd)] flex items-center justify-center" data-testid="public-menu-toggle">
            {open ? <X size={16} /> : <Menu size={16} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[var(--bd)] px-4 py-3 flex flex-col gap-1">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="px-3 py-2 rounded-md text-sm text-[var(--t2)] hover:bg-[var(--surface2)]">{n.label}</Link>
          ))}
          {!user && <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-2 rounded-md text-sm text-[var(--t2)]">Sign In</Link>}
        </div>
      )}
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer className="border-t border-[var(--bd)] bg-[var(--surface)]/40">
      <div className="max-w-[1300px] mx-auto px-6 py-10 grid grid-cols-1 sm:grid-cols-3 gap-8">
        <div>
          <div className="font-heading font-black text-lg flex items-center gap-2"><Zap size={18} className="text-[var(--accent)]" /> Campus Energy Monitor</div>
          <p className="text-sm text-[var(--t2)] mt-3 leading-relaxed">Production-grade IoT platform for 3-phase campus energy metering, control and AI-assisted analytics. CPUT Final Year Project 2026.</p>
        </div>
        <div>
          <div className="uppercase tracking-[0.2em] text-[10px] font-bold text-[var(--t2)] mb-3">Pages</div>
          <div className="flex flex-col gap-2 text-sm">
            <Link to="/features" className="text-[var(--t2)] hover:text-[var(--t1)]">Features</Link>
            <Link to="/gallery" className="text-[var(--t2)] hover:text-[var(--t1)]">Gallery</Link>
            <Link to="/code" className="text-[var(--t2)] hover:text-[var(--t1)]">Code</Link>
            <Link to="/publications" className="text-[var(--t2)] hover:text-[var(--t1)]">Publications</Link>
            <Link to="/about" className="text-[var(--t2)] hover:text-[var(--t1)]">About</Link>
            <Link to="/contact" className="text-[var(--t2)] hover:text-[var(--t1)]">Contact</Link>
            <Link to="/terms" className="text-[var(--t2)] hover:text-[var(--t1)]">Terms & Conditions</Link>
            <Link to="/privacy" className="text-[var(--t2)] hover:text-[var(--t1)]">Privacy Policy</Link>
          </div>
        </div>
        <div>
          <div className="uppercase tracking-[0.2em] text-[10px] font-bold text-[var(--t2)] mb-3">Connect</div>
          <div className="flex gap-2">
            {[[Github, "https://github.com/henockhnk092-dot"], [Youtube, "https://youtube.com/@HNK2005"], [Linkedin, "https://linkedin.com"], [MessageCircle, "https://wa.me/27754531797"]].map(([Icon, href], i) => (
              <a key={i} href={href} target="_blank" rel="noreferrer" className="w-9 h-9 rounded-md border border-[var(--bd)] flex items-center justify-center text-[var(--t2)] hover:text-[var(--accent)] hover:border-[var(--accent)] transition-colors"><Icon size={16} /></a>
            ))}
          </div>
        </div>
      </div>
      <div className="border-t border-[var(--bd)] py-4 text-center text-[10px] sm:text-xs text-[var(--t2)] font-mono px-4 break-words">© 2026 HNK · CPUT · Cape Town, South Africa</div>
    </footer>
  );
}

export function PublicLayout({ children }) {
  return (
    <div className="min-h-[100dvh] bg-[var(--bg)] text-[var(--t1)] flex flex-col">
      <PublicNav />
      <div className="flex-1">{children}</div>
      <PublicFooter />
      <ChatWidget />
    </div>
  );
}
